import * as t from 'io-ts';

import { ConsentPreferencesBody } from '@transcend-io/airgap.js-types';

import {
  ConsentLastKey,
  ConsentMetadata,
  EnrichedPreferenceStoreIdentifier,
  PreferenceStoreConsentDetailsWithNulls,
  PreferenceStoreConsentDetailsWithNullsV2,
  PreferenceStoreNoPurposesNoMetadataCodec,
  PreferenceStorePurposes,
  PurposePreferenceEntry,
  PurposeToPreferences,
} from './baseCodecs';
import { LambdaErrorReturn } from './lambdaCodecs';

// ////////////////////////////////////////
// ///// airgap.js sync lambda Codecs /////
// ////////////////////////////////////////

/**
 * The input request body that airgap.js sends to sync preferences.
 *
 * TODO: Rename to SyncPreferencesRecordInput
 */
export const PreferencesBody = t.intersection([
  ConsentPreferencesBody,
  t.partial({
    purposeToPreferences: PurposeToPreferences,
  }),
]);

/** Override types. */
export type PreferencesBody = t.TypeOf<typeof PreferencesBody>;

/**
 * THe response returned by the sync preferences lambda.
 *
 * TODO: Rename to SyncPreferencesRecordResponse
 */
export const PreferenceStoreRecord = t.intersection([
  PreferenceStoreNoPurposesNoMetadataCodec,
  t.type({
    /** The purpose associated to the user's record */
    purposes: PreferenceStorePurposes,
  }),
  t.partial({
    /** A mapping of purposes to their associated preferences */
    purposeToPreferences: PurposeToPreferences,
    /** metadata associated to the user consent */
    metadata: ConsentMetadata,
  }),
]);

/** Override types. */
export type PreferenceStoreRecord = t.TypeOf<typeof PreferenceStoreRecord>;

// ////////////////////////////////////////
// ///// Update Preferences Codecs ////////
// ////////////////////////////////////////

/**
 *  Request to update consent preferences.
 *
 * @deprecated Use `UpdateConsentDataV3` instead
 * TODO: https://linear.app/transcend/issue/PIK-315 - Clean up types after backend uses new contract
 */
export const UpdateConsentDataV2 = t.intersection([
  t.type({
    /** partition */
    partition: t.string,
    /** Airgap consent details */
    consent: PreferenceStoreConsentDetailsWithNulls,
    /** Identifiers of the user */
    identifiers: t.array(EnrichedPreferenceStoreIdentifier),
  }),
  t.partial({
    /**
     * Encrypted identifier
     */
    encryptedIdentifier: t.string,
  }),
]);

/** Override types. */
export type UpdateConsentDataV2 = t.TypeOf<typeof UpdateConsentDataV2>;

export const UpdateConsentDataV3 = t.intersection([
  t.type({
    /** partition */
    partition: t.string,
    /** Airgap consent details */
    consent: PreferenceStoreConsentDetailsWithNullsV2,
    /** Identifiers of the user */
    identifiers: t.array(EnrichedPreferenceStoreIdentifier),
  }),
  t.partial({
    /**
     * Encrypted identifier
     */
    encryptedIdentifier: t.string,
  }),
]);

/** Override types. */
export type UpdateConsentDataV3 = t.TypeOf<typeof UpdateConsentDataV3>;

/**
 * API response of a single preference store record from Admin query/update lambdas
 *
 * Note:
 * - Purposes are record of purpose to boolean mapping
 * - Preference for purpose are nested under purposeToPreferences
 *
 * @deprecated Use `AdminPreferenceStoreRecordV2` instead
 *
 * TODO: https://linear.app/transcend/issue/PIK-315 - Clean up types after backend uses new contract
 */
export const AdminPreferenceStoreRecord = t.intersection([
  PreferenceStoreNoPurposesNoMetadataCodec,
  t.type({
    /** The purpose associated to the user's record */
    purposes: PreferenceStorePurposes,
  }),
  t.partial({
    /** A mapping of purposes to their associated preferences */
    purposeToPreferences: PurposeToPreferences,
    /** The metadata associated to the user's record */
    metadata: t.string,
  }),
]);

/** Override types. */
export type AdminPreferenceStoreRecord = t.TypeOf<
  typeof AdminPreferenceStoreRecord
>;

/**
 * API response of a single preference store record from Admin update lambdas
 *
 * Note:
 * - Purposes are an array of purpose records including consent and preferences
 * - This is the preferred format to use in the admin APIs
 */
export const AdminPreferenceStoreRecordV2 = t.intersection([
  PreferenceStoreNoPurposesNoMetadataCodec,
  t.type({
    /** The purpose associated to the user's record */
    purposes: t.array(PurposePreferenceEntry),
  }),
  t.partial({
    /** The metadata associated to the user's record */
    metadata: t.string,
  }),
]);

/** Override types. */
export type AdminPreferenceStoreRecordV2 = t.TypeOf<
  typeof AdminPreferenceStoreRecordV2
>;

/** Information about a failed update in the admin update handler */
export const AdminPreferencesFailedUpdate = t.type({
  /** The index of the failed update in the original request */
  index: t.number,
  /** The error message associated with the failure */
  error: t.string,
});

/** Override types. */
export type AdminPreferencesFailedUpdate = t.TypeOf<
  typeof AdminPreferencesFailedUpdate
>;

/**
 * The shape of the input to the admin update handler lambda.
 */
export const AdminUpdateHandlerInput = t.type({
  /** Array of consent preferences to update */
  data: t.array(t.union([UpdateConsentDataV2, UpdateConsentDataV3])),
});

/** Override types. */
export type AdminUpdateHandlerInput = t.TypeOf<typeof AdminUpdateHandlerInput>;

/**
 * The shape of the response from the admin update handler lambda.
 */
export const AdminUpdateHandlerResponse = t.intersection([
  t.partial({
    /**
     * Array of updated consent preferences
     *
     * @deprecated Use `records` with purposes array instead
     */
    data: t.array(AdminPreferenceStoreRecord),
    /** The response array that includes updated consent records */
    records: t.array(AdminPreferenceStoreRecordV2),
    /** Array of failed updates with their respective errors */
    failedUpdates: t.array(AdminPreferencesFailedUpdate),
  }),
  LambdaErrorReturn,
]);

/** Override types. */
export type AdminUpdateHandlerResponse = t.TypeOf<
  typeof AdminUpdateHandlerResponse
>;

// ////////////////////////////////////////
// ///// Query Preferences Codecs /////////
// ////////////////////////////////////////

/** Base response structure shared by all query types */
const BaseQueryResponse = t.type({
  nodes: t.array(AdminPreferenceStoreRecord),
});

/** Cursor type for shared identifier queries */
export const SharedIdentifierCursor = t.intersection([
  t.type({
    userId: t.string,
    partition: t.string,
  }),
  t.partial({
    linkedIdentifier: t.string,
  }),
]);

/** Cursor type for unique identifier queries */
export const UniqueIdentifierCursor = t.type({
  userId: t.string,
  partition: t.string,
});

/** Cursor type for partition queries */
export const PartitionCursor = t.intersection([
  t.type({
    userId: t.string,
    partition: t.string,
  }),
  t.partial({
    timestamp: t.string,
    updatedAt: t.string,
  }),
]);

/** Response for shared identifier queries */
export const SharedIdentifierQueryResponse = t.intersection([
  BaseQueryResponse,
  t.partial({
    /** Cursor for pagination using linkedIdentifier */
    lastKey: SharedIdentifierCursor,
  }),
]);

/** Response for unique identifier queries */
export const UniqueIdentifierQueryResponse = t.intersection([
  BaseQueryResponse,
  t.partial({
    /** Cursor for pagination using userId and partition */
    lastKey: UniqueIdentifierCursor,
    /** Map showing which identifier values map to which user IDs */
    identifierMapping: t.record(t.string, t.array(t.string)),
  }),
]);

/** Response for partition queries */
export const PartitionQueryResponse = t.intersection([
  BaseQueryResponse,
  t.partial({
    /** Cursor for pagination using userId, partition, and timestamp/updatedAt */
    lastKey: PartitionCursor,
  }),
]);

/** Union of all possible response types based on query strategy */
export const QueryConsentPreferencesResponse = t.union([
  SharedIdentifierQueryResponse,
  UniqueIdentifierQueryResponse,
  PartitionQueryResponse,
]);

/** TypeScript type aliases for better developer experience */

/** Type alias for shared identifier cursor */
export type SharedIdentifierCursor = t.TypeOf<typeof SharedIdentifierCursor>;

/** Type alias for unique identifier cursor */
export type UniqueIdentifierCursor = t.TypeOf<typeof UniqueIdentifierCursor>;

/** Type alias for partition cursor */
export type PartitionCursor = t.TypeOf<typeof PartitionCursor>;

/** Type alias for shared identifier query response */
export type SharedIdentifierQueryResponse = t.TypeOf<
  typeof SharedIdentifierQueryResponse
>;

/** Type alias for unique identifier query response */
export type UniqueIdentifierQueryResponse = t.TypeOf<
  typeof UniqueIdentifierQueryResponse
>;

/** Type alias for partition query response */
export type PartitionQueryResponse = t.TypeOf<typeof PartitionQueryResponse>;

/** Type alias for the union of all query response types */
export type QueryConsentPreferencesResponse = t.TypeOf<
  typeof QueryConsentPreferencesResponse
>;

/**
 * The response returned by the admin query preferences lambda.
 */
export const ConsentPreferenceResponse = t.intersection([
  t.partial({
    /**
     * Array of consent preferences where purposes is a record of purpose to boolean mapping
     *
     * @deprecated Use `records` with purposes array instead
     */
    nodes: t.array(AdminPreferenceStoreRecord),
    /** Array of updated consent preferences */
    records: t.array(AdminPreferenceStoreRecordV2),
    /** Last key for pagination */
    lastKey: ConsentLastKey,
    /** The mapping of lookup identifiers to primary keys */
    requestIdentifierToPrimaryKeysMap: t.record(t.string, t.array(t.string)),
  }),
  LambdaErrorReturn,
]);

/** Override types. */
export type ConsentPreferenceResponse = t.TypeOf<
  typeof ConsentPreferenceResponse
>;
